import React, { useState, useEffect } from 'react';
import { t } from './translations';

const CARD_W = 1080;
const CARD_H = 1080;

const THEMES = [
  { id: 'midnight', label: { en: 'Midnight Gold', ar: 'ذهب الليل' },
    bg: ['#0D0A14','#1C1830'], accent: '#FFD166', text: '#FFF8EC', sub: '#8A7A6A' },
  { id: 'rose',     label: { en: 'Rose Glow',    ar: 'وردي' },
    bg: ['#1A0A12','#2A1020'], accent: '#FF8FAB', text: '#FFF0F5', sub: '#8A6A72' },
  { id: 'teal',     label: { en: 'Ocean Calm',   ar: 'هدوء المحيط' },
    bg: ['#061414','#0A2020'], accent: '#2DD4BF', text: '#F0FFFE', sub: '#5A8A85' },
  { id: 'cream',    label: { en: 'Warm Cream',   ar: 'كريمي دافي' },
    bg: ['#1C1408','#2A1E0A'], accent: '#FFE580', text: '#FFF8EC', sub: '#8A7A5A' },
];

// Preset captions the owner can pick, plus custom
const CAPTIONS = {
  en: [
    { id: 'look',    text: 'Look what people say about me 🫙' },
    { id: 'anon',    text: 'Someone sent me this anonymously ✨' },
    { id: 'feels',   text: 'This made my whole day 💛' },
    { id: 'jar',     text: 'From my @MyGlow.me jar 🫙' },
    { id: 'kind',    text: 'Proof that kindness exists 🌸' },
    { id: 'custom',  text: '' },
  ],
  ar: [
    { id: 'look',    text: 'شوفوا إيه اللي الناس بتقوله عني 🫙' },
    { id: 'anon',    text: 'حد بعتلي ده بشكل مجهول ✨' },
    { id: 'feels',   text: 'ده عمل يومي كله حلو 💛' },
    { id: 'jar',     text: 'من جرّتي على MyGlow.me 🫙' },
    { id: 'kind',    text: 'دليل إن في ناس حلوة في الدنيا 🌸' },
    { id: 'custom',  text: '' },
  ],
};

function drawRoundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x+r,y); ctx.lineTo(x+w-r,y);
  ctx.quadraticCurveTo(x+w,y,x+w,y+r); ctx.lineTo(x+w,y+h-r);
  ctx.quadraticCurveTo(x+w,y+h,x+w-r,y+h); ctx.lineTo(x+r,y+h);
  ctx.quadraticCurveTo(x,y+h,x,y+h-r); ctx.lineTo(x,y+r);
  ctx.quadraticCurveTo(x,y,x+r,y); ctx.closePath();
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  // Handle RTL/LTR mixed content
  const words = text.split(' ');
  let line='', lines=[];
  for(let n=0;n<words.length;n++){
    const test=line+words[n]+' ';
    if(ctx.measureText(test).width>maxWidth&&n>0){lines.push(line.trim());line=words[n]+' ';}
    else line=test;
  }
  lines.push(line.trim());
  const totalH=lines.length*lineHeight;
  const startY=y-totalH/2;
  lines.forEach((l,i)=>ctx.fillText(l,x,startY+i*lineHeight+lineHeight*0.5));
  return lines.length;
}

export function generateGlowCard(msg, theme, lang, caption='') {
  return new Promise(resolve => {
    const canvas=document.createElement('canvas');
    canvas.width=CARD_W; canvas.height=CARD_H;
    const ctx=canvas.getContext('2d');
    const isAr=lang==='ar';

    // ── Background ──
    const grad=ctx.createLinearGradient(0,0,CARD_W,CARD_H);
    grad.addColorStop(0,theme.bg[0]); grad.addColorStop(1,theme.bg[1]);
    ctx.fillStyle=grad; ctx.fillRect(0,0,CARD_W,CARD_H);

    // Radial center glow
    const radial=ctx.createRadialGradient(CARD_W/2,CARD_H/2,0,CARD_W/2,CARD_H/2,480);
    radial.addColorStop(0,theme.accent+'18'); radial.addColorStop(1,'transparent');
    ctx.fillStyle=radial; ctx.fillRect(0,0,CARD_W,CARD_H);

    // ── Sparkle crosses ──
    [[120,120],[960,140],[80,900],[980,920],[540,60],[190,480],[890,490],[300,960],[750,940]].forEach(([sx,sy])=>{
      ctx.save(); ctx.globalAlpha=0.3; ctx.strokeStyle=theme.accent; ctx.lineWidth=2.5; ctx.lineCap='round';
      const s=13;
      ctx.beginPath(); ctx.moveTo(sx,sy-s); ctx.lineTo(sx,sy+s); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(sx-s,sy); ctx.lineTo(sx+s,sy); ctx.stroke();
      ctx.restore();
    });

    // ── Caption banner at top (if set) ──
    const hasCaption=caption&&caption.trim().length>0;
    const captionH=hasCaption?100:0;

    if(hasCaption){
      // Semi-transparent pill behind caption
      ctx.save();
      drawRoundRect(ctx,60,52,CARD_W-120,72,36);
      ctx.fillStyle='rgba(0,0,0,0.35)'; ctx.fill();
      ctx.strokeStyle=theme.accent+'55'; ctx.lineWidth=1.5; ctx.stroke();
      ctx.restore();
      ctx.fillStyle=theme.accent;
      ctx.textAlign='center';
      ctx.font=`${isAr?34:30}px ${isAr?'Tajawal,Arial':'Helvetica,Arial'}`;
      ctx.fillText(caption, CARD_W/2, 52+44);
    }

    // ── Inner card panel ──
    const pad=72;
    const cardY=pad+captionH+24;
    const cardH=CARD_H-cardY-pad-120;
    ctx.save();
    drawRoundRect(ctx,pad,cardY,CARD_W-pad*2,cardH,44);
    ctx.fillStyle='rgba(255,255,255,0.055)'; ctx.fill();
    ctx.strokeStyle=theme.accent+'50'; ctx.lineWidth=2; ctx.stroke();
    ctx.restore();

    // Top accent bar on card
    ctx.save();
    drawRoundRect(ctx,pad,cardY,CARD_W-pad*2,7,3);
    const barG=ctx.createLinearGradient(pad,0,CARD_W-pad,0);
    barG.addColorStop(0,theme.accent); barG.addColorStop(0.5,theme.accent+'BB'); barG.addColorStop(1,theme.accent);
    ctx.fillStyle=barG; ctx.fill();
    ctx.restore();

    // ── Emoji ──
    ctx.font='110px serif';
    ctx.textAlign='center';
    ctx.fillText(msg.emoji||'✨', CARD_W/2, cardY+150);

    // ── Quote text ──
    ctx.fillStyle=theme.text;
    ctx.textAlign='center';
    ctx.font=`italic ${isAr?50:46}px ${isAr?'Tajawal,Arial':'Georgia,serif'}`;
    wrapText(ctx, `"${msg.text}"`, CARD_W/2, cardY+330, CARD_W-pad*2-100, 70);

    // ── Anon line ──
    ctx.fillStyle=theme.sub;
    ctx.font=`26px ${isAr?'Tajawal,Arial':'Helvetica,Arial'}`;
    ctx.fillText(isAr?'— ضو مجهول':'— anonymous glow', CARD_W/2, cardY+cardH-70);

    // ── Bottom branding ──
    ctx.fillStyle=theme.accent;
    ctx.font='bold 34px Helvetica,Arial';
    ctx.fillText('MyGlow.me', CARD_W/2, CARD_H-68);
    ctx.fillStyle=theme.sub;
    ctx.font='24px Helvetica,Arial';
    ctx.fillText(isAr?'احفظ أضواءك ✨':'save your glows ✨', CARD_W/2, CARD_H-32);

    resolve(canvas.toDataURL('image/png'));
  });
}

// ─── GLOW CARD MODAL ─────────────────────────────────────────────────────────
export function GlowCardModal({ msg, lang, onClose }) {
  const T = t[lang];
  const [selectedTheme, setSelectedTheme] = useState(THEMES[0]);
  const [cardDataUrl, setCardDataUrl] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [selectedCaption, setSelectedCaption] = useState(CAPTIONS[lang][0]);
  const [customCaption, setCustomCaption] = useState('');

  const activeCaption = selectedCaption.id === 'custom' ? customCaption : selectedCaption.text;

  // Regenerate whenever theme or caption changes
  useEffect(() => { generate(selectedTheme, activeCaption); }, []);

  async function generate(theme, caption) {
    setGenerating(true);
    const url = await generateGlowCard(msg, theme, lang, caption);
    setCardDataUrl(url);
    setGenerating(false);
  }

  function handleTheme(theme) {
    setSelectedTheme(theme);
    generate(theme, activeCaption);
  }

  function handleCaption(cap) {
    setSelectedCaption(cap);
    const txt = cap.id === 'custom' ? customCaption : cap.text;
    generate(selectedTheme, txt);
  }

  function handleCustomCaption(val) {
    setCustomCaption(val);
    if (selectedCaption.id === 'custom') generate(selectedTheme, val);
  }

  function downloadCard() {
    if (!cardDataUrl) return;
    const a = document.createElement('a');
    a.href = cardDataUrl;
    a.download = 'my-glow.png';
    a.click();
  }

  async function shareCard() {
    if (!cardDataUrl || !navigator.share) { downloadCard(); return; }
    try {
      const res = await fetch(cardDataUrl);
      const blob = await res.blob();
      const file = new File([blob], 'my-glow.png', { type: 'image/png' });
      await navigator.share({
        files: [file],
        title: lang==='ar' ? 'ضوي من MyGlow.me ✨' : 'My glow from MyGlow.me ✨',
        text: activeCaption || (lang==='ar' ? 'احفظ أضواءك على MyGlow.me 🫙' : 'Save your glows at MyGlow.me 🫙'),
      });
    } catch { downloadCard(); }
  }

  useEffect(()=>{
    function h(e){if(e.key==='Escape')onClose();}
    window.addEventListener('keydown',h); return()=>window.removeEventListener('keydown',h);
  },[onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} dir={T.dir}
        style={{background:'var(--card)',border:'1px solid rgba(255,209,102,0.28)',borderRadius:24,padding:'24px',maxWidth:500,width:'100%',boxShadow:'0 20px 70px rgba(0,0,0,0.75)',maxHeight:'90vh',overflowY:'auto'}}>

        {/* Header */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:18}}>
          <div style={{fontWeight:600,fontSize:'1rem'}}>
            {lang==='ar'?'🖼 شارك ضوك':'🖼 Share your glow'}
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',color:'var(--text-muted)',fontSize:'1.1rem',cursor:'pointer',padding:'4px 8px'}}>✕</button>
        </div>

        {/* Card preview */}
        <div style={{borderRadius:14,overflow:'hidden',marginBottom:16,background:'var(--bg3)',minHeight:160,display:'flex',alignItems:'center',justifyContent:'center',position:'relative'}}>
          {generating && (
            <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.4)',borderRadius:14,zIndex:2}}>
              <div style={{color:'var(--gold)',fontSize:'1.4rem'}}>✨</div>
            </div>
          )}
          {cardDataUrl && <img src={cardDataUrl} alt="glow card" style={{width:'100%',display:'block',borderRadius:14}}/>}
        </div>

        {/* Caption picker */}
        <div style={{marginBottom:16}}>
          <div style={{fontSize:'0.72rem',color:'var(--text-muted)',letterSpacing:'0.06em',textTransform:'uppercase',marginBottom:8}}>
            {lang==='ar'?'الكابشن':'Caption'}
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            {CAPTIONS[lang].map(cap=>(
              <button key={cap.id} onClick={()=>handleCaption(cap)}
                style={{textAlign:T.dir==='rtl'?'right':'left',padding:'9px 14px',borderRadius:10,border:`1.5px solid ${selectedCaption.id===cap.id?'var(--gold)':'rgba(255,255,255,0.1)'}`,background:selectedCaption.id===cap.id?'rgba(255,209,102,0.1)':'transparent',color:selectedCaption.id===cap.id?'var(--gold)':'var(--text-muted)',fontSize:'0.82rem',cursor:'pointer',fontFamily:'inherit',transition:'all 0.15s'}}>
                {cap.id==='custom' ? (lang==='ar'?'✏️ كابشن مخصص…':'✏️ Custom caption…') : cap.text}
              </button>
            ))}
          </div>
          {selectedCaption.id==='custom' && (
            <input
              type="text"
              value={customCaption}
              onChange={e=>handleCustomCaption(e.target.value)}
              placeholder={lang==='ar'?'اكتب كابشنك…':'Write your caption…'}
              maxLength={80}
              style={{marginTop:8,textAlign:T.dir==='rtl'?'right':'left'}}
              autoFocus
            />
          )}
          {/* No caption option */}
          <button onClick={()=>{setSelectedCaption({id:'none',text:''});generate(selectedTheme,'');}}
            style={{marginTop:6,padding:'6px 12px',borderRadius:8,border:'1px solid rgba(255,255,255,0.08)',background:'transparent',color:'var(--text-muted)',fontSize:'0.75rem',cursor:'pointer',fontFamily:'inherit'}}>
            {lang==='ar'?'بدون كابشن':'No caption'}
          </button>
        </div>

        {/* Theme picker */}
        <div style={{marginBottom:18}}>
          <div style={{fontSize:'0.72rem',color:'var(--text-muted)',letterSpacing:'0.06em',textTransform:'uppercase',marginBottom:8}}>
            {lang==='ar'?'الثيم':'Theme'}
          </div>
          <div style={{display:'flex',gap:7,flexWrap:'wrap'}}>
            {THEMES.map(th=>(
              <button key={th.id} onClick={()=>handleTheme(th)}
                style={{padding:'6px 14px',borderRadius:100,border:`1.5px solid ${selectedTheme.id===th.id?th.accent:'rgba(255,255,255,0.12)'}`,background:selectedTheme.id===th.id?`${th.accent}22`:'transparent',color:selectedTheme.id===th.id?th.accent:'var(--text-muted)',fontSize:'0.78rem',cursor:'pointer',transition:'all 0.15s',fontFamily:'inherit'}}>
                {th.label[lang]}
              </button>
            ))}
          </div>
        </div>

        {/* Share / Download */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          <button className="btn btn-glow" onClick={shareCard} style={{fontSize:'0.88rem'}} disabled={generating}>
            {lang==='ar'?'📤 شارك':'📤 Share'}
          </button>
          <button className="btn btn-ghost" onClick={downloadCard} style={{fontSize:'0.88rem'}} disabled={generating}>
            {lang==='ar'?'⬇️ حمّل':'⬇️ Download'}
          </button>
        </div>

        <p style={{marginTop:10,textAlign:'center',fontSize:'0.72rem',color:'var(--text-muted)'}}>
          {lang==='ar'
            ?'انستجرام · تيك توك · واتساب · فيسبوك'
            :'Instagram · TikTok · WhatsApp · Facebook'}
        </p>
      </div>
    </div>
  );
}
